import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    datePeriodicityOptions,
    getTranslatedSocialMetricOptions,
    platformOptions,
    getTranslatedAllAccountTypeOptions,
    visualizationOptions,
} from './socialbenchmarking-filters';
import getOptions from '../utils/getFilterOptions';
import connect from '../utils/connect';
import { FOLLOWER_METRIC, POST_METRIC } from '../constants/sbMetrics';
import { getDateDifference } from '../utils/getDateDifference';

export const SocialBenchmarkingContext = React.createContext(null);

interface SocialBenchmarkingProviderProps {
    children: React.ReactNode;
    filters: any;
}

interface customDateRangeType {
    startDate: string | null;
    endDate: string | null;
}

// Default value for social metric (always 'post' - first option)
const DEFAULT_SOCIAL_METRIC_VALUE = 'post';

const UnconnectedSocialBenchmarkingProvider = ({
    children,
    filters,
}: SocialBenchmarkingProviderProps) => {
    const { t, i18n } = useTranslation('benchmarking');
    const { mediaTypes } = filters;
    const mediaTypesList = mediaTypes.list;

    // Get translated social metric options
    const socialMetricOptions = useMemo(
        () => getTranslatedSocialMetricOptions(t),
        [t],
    );

    // Get translated account type options (base options for filtering)
    // Use t, i18n, and i18n.language as dependencies to ensure updates when language changes
    const translatedAccountTypeOptions = useMemo(
        () => getTranslatedAllAccountTypeOptions(t),
        [t, i18n, i18n.language],
    );

    const [socialMetricFilterValue, setSocialMetricFilterValue] = useState(
        DEFAULT_SOCIAL_METRIC_VALUE,
    );
    const [datePeriodicityValue, setDatePeriodicityValue] = useState(
        datePeriodicityOptions[0].value,
    );
    const [leagueFilterValue, setLeagueFilterValue] = useState(null);
    const [selectedVisualizationOption, setSelectedVisualizationOption] =
        useState(visualizationOptions[0].value);
    const [sbTitle, setSbTitle] = useState('');
    const [selectedAccountGroup, setSelectedAccountGroup] = useState(null);
    const [selectedHeader, setSelectedHeader] = useState('itemCount');
    const [mediaTypesValues, setMediaTypesValues] = useState([]);
    const [accountTypeValues, setAccountTypeValues] = useState([]);
    const [accountTypeOptions, setAccountTypeOptions] = useState(
        translatedAccountTypeOptions,
    );

    // Sync accountTypeOptions with translatedAccountTypeOptions when language changes
    // Preserves the current filtering (only updates labels for account types already in the list)
    useEffect(() => {
        const currentIds = accountTypeOptions.map(opt => opt.value);
        const updatedOptions = translatedAccountTypeOptions.filter(opt =>
            currentIds.includes(opt.value),
        );
        setAccountTypeOptions(updatedOptions);
    }, [translatedAccountTypeOptions]);

    const [currentPageData, setCurrentPageData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [tableSorting, setTableSorting] = useState([
        {
            id: 'rank',
            desc: false,
        },
    ]);
    const [platformsValues, setPlaformsValues] = useState([]);
    const [currentSocialMetricFilterValue, setCurrentSocialMetricFilterValue] =
        useState(DEFAULT_SOCIAL_METRIC_VALUE);

    const [currentVisualizationValue, setCurrentVisualizationValue] = useState(
        visualizationOptions[0].value,
    );

    // Submitted/applied filter values (what's actually being used for data fetching)
    const [appliedFilters, setAppliedFilters] = useState({
        socialMetric: DEFAULT_SOCIAL_METRIC_VALUE,
        league: null,
        datePeriodicity: datePeriodicityOptions[0].value,
        platforms: [],
        accountTypes: [],
        mediaTypes: [],
        visualization: visualizationOptions[0].value,
        customDateRange: { startDate: null, endDate: null },
    });
    const [customDateRange, setCustomDateRange] = useState<customDateRangeType>(
        {
            startDate: null,
            endDate: null,
        },
    );

    const mediaOptions = useMemo(() => {
        const allOptions = getOptions({ list: mediaTypesList });
        const filteredMediaOptions = allOptions.filter(
            item => item.label !== 'N/A',
        );
        return filteredMediaOptions;
    }, [filters]);

    const mediaTypeOptionIds = useMemo(
        () => mediaOptions.map(mediaOption => mediaOption.value),
        [mediaOptions],
    );
    const platformOptionIds = useMemo(
        () => platformOptions.map(platformOption => platformOption.value),
        [platformOptions],
    );

    const accountTypeOptionIds = useMemo(
        () =>
            accountTypeOptions.map(
                accountTypeOption => accountTypeOption.value,
            ),
        [accountTypeOptions],
    );

    const socialMetric = useMemo(() => {
        if (!socialMetricOptions || !currentSocialMetricFilterValue) return '';

        const socialMetricValue = socialMetricOptions.find(
            ({ value }) => value === currentSocialMetricFilterValue,
        );

        return socialMetricValue ? socialMetricValue.name : '';
    }, [socialMetricOptions, currentSocialMetricFilterValue]);

    const isFollowerMetric = socialMetricFilterValue === FOLLOWER_METRIC;

    const isMediaTypeVisual = selectedVisualizationOption === 'mediaType';

    const isFollowerMetricCurrent =
        currentSocialMetricFilterValue === FOLLOWER_METRIC;

    const isPostMetricCurrent = currentSocialMetricFilterValue === POST_METRIC;

    const periodicity = useMemo(() => {
        if (datePeriodicityValue === 'weekly') {
            return t('socialBenchmarking.periodicity.weekOverWeek');
        } else if (datePeriodicityValue === 'monthly') {
            return t('socialBenchmarking.periodicity.monthOverMonth');
        } else {
            const days = getDateDifference(
                customDateRange.startDate,
                customDateRange.endDate,
            );
            return t('socialBenchmarking.periodicity.custom', { days });
        }
    }, [datePeriodicityValue, customDateRange, t]);

    const providerValue = {
        socialMetricFilterValue,
        currentSocialMetricFilterValue,
        currentVisualizationValue,
        datePeriodicityValue,
        leagueFilterValue,
        selectedVisualizationOption,
        selectedAccountGroup,
        selectedHeader,
        mediaTypesValues,
        platformsValues,
        accountTypeValues,
        accountTypeOptions,
        translatedAccountTypeOptions,
        setSocialMetricFilterValue,
        setDatePeriodicityValue,
        setLeagueFilterValue,
        setSelectedVisualizationOption,
        setSelectedAccountGroup,
        setSelectedHeader,
        setMediaTypesValues,
        setPlaformsValues,
        setAccountTypeValues,
        setAccountTypeOptions,
        socialMetric,
        isFollowerMetric,
        isFollowerMetricCurrent,
        isPostMetricCurrent,
        isMediaTypeVisual,
        periodicity,
        setCurrentVisualizationValue,
        setCurrentSocialMetricFilterValue,
        sbTitle,
        setSbTitle,
        mediaOptions,
        mediaTypeOptionIds,
        platformOptionIds,
        accountTypeOptionIds,
        tableSorting,
        setTableSorting,
        customDateRange,
        setCustomDateRange,
        appliedFilters,
        setAppliedFilters,
        currentPageData,
        setCurrentPageData,
        currentPage,
        setCurrentPage,
    };

    return (
        <SocialBenchmarkingContext.Provider value={providerValue}>
            {children}
        </SocialBenchmarkingContext.Provider>
    );
};

export const SocialBenchmarkingProvider = connect(['filters'])(
    UnconnectedSocialBenchmarkingProvider,
);
